import { styled, Typography } from "@mui/material";
import { apiDeleteMedia, apiUploadMedia } from "api/mediaApi";
import PlusIcon from "assets/icons/PlusIcon";
import { StyledAvatar } from "components/Shared/Dashboard/styled";
import { useFormikContext } from "formik";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BasicMenu from "components/Menu/Basic";
import EditImg from "assets/images/Edit.svg";
import DeleteImg from "assets/images/Delete.svg";

const backgroundImage =
  "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23AEAEAEFF' strokeWidth='4' stroke-dasharray='14 8' stroke-dashoffset='32' stroke-linecap='square'/%3e%3c/svg%3e";
const roundedBackgroundImage =
  "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='83' ry='83' stroke='%23AEAEAEFF' strokeWidth='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e";

const ButtonContainer = styled("div")(
  ({ width, height, rounded }: { width: number | string; height: number | string; rounded?: boolean }) => ({
    miWidth: width,
    width: width,
    position: "relative",
    height: height,
    borderRadius: rounded ? "50%" : "4px",
    backgroundImage: `url("${rounded ? roundedBackgroundImage : backgroundImage}")`,
    backgroundSize: "100% 100%",
    backgroundColor: "unset",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  })
);
const InnerButton = styled("div")(
  ({
    rounded,
    width,
    height,
    horizontal,
    hovered,
  }: {
    width: string | number;
    height: string | number;
    horizontal?: boolean;
    rounded?: boolean;
    hovered?: boolean;
  }) => ({
    width: width,
    height: height,
    borderRadius: rounded ? "50%" : "4px",
    backgroundColor: "#F5F5F5",
    display: "flex",
    flexDirection: horizontal ? "row" : "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: hovered ? "#e4e3e3 0px 0px 20px 5px" : "unset",
  })
);

interface roundedInterface {
  rounded?: boolean;
}

const ProgressContainer = styled("div")<roundedInterface>(({ theme, rounded }) => ({
  width: "100%",
  height: "100%",
  borderRadius: rounded ? "50%" : "unset",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#72717158",
}));
const Layer = styled("div")<roundedInterface>(({ theme, rounded }) => ({
  width: "100%",
  height: "100%",
  borderRadius: rounded ? "50%" : "unset",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff90",
}));
const AddButton = styled("div")(({ theme }) => ({
  width: "40px",
  height: "40px",
  backgroundColor: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  cursor: "pointer",
}));

const getInitialImage = (name: string, values: any) => {
  try {
    const image = values[name];
    return image;
  } catch {
    return "";
  }
};

interface Props {
  title?: string;
  name: string;
  width: number | string;
  height: number | string;
  innerWidth: number | string;
  innerHeight: number | string;
  LogoPlaceholder: React.FC;
  horizontal?: boolean;
  rounded?: boolean;
  disableLayer?: boolean;
}

const UploadImageComponent = (props: Props) => {
  const {
    title,
    name,
    width,
    height,
    innerWidth,
    innerHeight,
    LogoPlaceholder,
    horizontal,
    rounded,
    disableLayer,
  } = props;
  const dispatch = useDispatch();
  const formikContext = useFormikContext();
  const { values } = formikContext;
  const [selectedFile, setSelectedFile] = useState<undefined | File>();
  const [preview, setPreview] = useState<undefined | string>(getInitialImage(name, values));
  const [hovered, setHovered] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const callBack = async (url: string) => {
    setIsUploading(false);
    setPreview(url);
    await formikContext.setFieldValue(name, url);
  };

  const token = useSelector((state: any) => state).businessReducer.business?.token;
  useEffect(() => {
    if (!selectedFile) {
      // setPreview(undefined)
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  async function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    setIsUploading(true);
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const url = URL.createObjectURL(file);
    setPreview(url);
    const formData = new FormData();
    //todo file Upload
    formData.append("files", file);
    try {
      await apiUploadMedia(formData, token, callBack, dispatch);
      setSelectedFile(file);
      e.target.value = "";
    } catch (error) {
      e.target.value = "";
    }
  }

  function handleInputClick() {
    // if (preview) return;
    inputRef.current?.click();
  }

  function handleRemove() {
    if (!preview) return;
    setSelectedFile(undefined);
    // @ts-ignore
    dispatch(apiDeleteMedia({ url: preview }, token, callBack));
  }

  return (
    <ButtonContainer onMouseLeave={() => setHovered(false)} rounded={rounded} width={width} height={height}>
      {!preview && (
        <InnerButton
          hovered={hovered}
          rounded={rounded}
          horizontal={horizontal}
          width={innerWidth}
          height={innerHeight}
        >
          <LogoPlaceholder />

          <div style={{ padding: "9px" }} />
          <Typography sx={{ fontWeight: 500 }} color="primary">
            {title}
          </Typography>
        </InnerButton>
      )}
      {preview && <StyledAvatar sx={{ borderRadius: rounded ? "50%" : "8px" }} src={preview} />}
      {isUploading && (
        <ProgressContainer rounded={rounded}>
          <CircularProgress />
        </ProgressContainer>
      )}
      {!disableLayer && !hovered && !preview && (
        <Layer onMouseEnter={() => setHovered(true)} rounded={rounded} />
      )}
      <div style={{ position: "absolute", bottom: "-20px" }}>
        <input ref={inputRef} hidden type="file" onChange={onSelectFile} />
        <BasicMenu
          items={[
            { text: "انتخاب عکس", icon: <img src={EditImg} alt="" />, onClick: handleInputClick },
            { text: "حذف", icon: <img src={DeleteImg} alt="" />, onClick: handleRemove },
          ]}
          Btn={
            <AddButton>
              <PlusIcon scale={0.8} />
            </AddButton>
          }
        />
      </div>
    </ButtonContainer>
  );
};

export default UploadImageComponent;
